import React, { useState, useEffect, Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Row, Col, ButtonToolbar } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TimePicker, Switch, Divider } from 'antd'
import moment from 'moment'
import {
  faInfoCircle,
  faSave,
  faArrowRight,
  faArrowLeft,
  faSpinner,
  faSpinnerThird,
  faEye,
  faRotate,
} from '@fortawesome/pro-solid-svg-icons'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router'

import TextInput from 'components/ui/forms/field/text-input/TextInput'
import { CourseStateType, ImgStatusEnum, CourseType } from 'enum'
import { getSelected } from 'helpers/react-select.helper'
import Button from 'design-system/button'
import { ButtonVariant, ButtonSizes } from 'design-system/button/src/Button'
import { ErrorToast, SuccessToast } from 'components/ui/toast/toastsTemplates'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import * as fromProfilesActions from 'store/actions/lms-profiles.action'
import useIntegrations from 'hooks/useIntegrations'
import { ICourseModel } from 'models/course.model'
import { useHasSence } from 'store/selectors'
import LinkExternalCourse from 'api/courses/LinkExternalCourse'
import { ICoursePermissions } from 'store/states/access.state'
import RichTextInput from '../field/rich-text-input/RichTextInput'
import FileInput from '../field/file-input/FileInput'
import SelectInput from '../field/select-input/SelectInput'
import DatePickerInput from '../field/date-picker-input/DatePickerInput'
import CheckBoxRadioButtonInput from '../field/checkbox-radiobutton/CheckBoxRadioButtonInput'
import InputLabel from '../field/input-label/InputLabel'
import { CreateNewCourseForm, OnSubmitValues } from './types'
import {
  CourseTypeInput,
  NotificationsInput,
  SynchronousCheckBox,
  Options,
} from './components/forms'
import { useForm, FormProvider, useFieldExternalIntegration } from './context'
import { defaultValues } from './common'
import { ShowDates } from './constants'
import { validate, imgFileValidate } from './validate'
import { Sence } from './components/forms/Sence'
import styles from './CourseForm.module.scss'
import EmailNotifications from './EmailNotifications'
import { Wrapper, Subtitle, RequiresApproval } from './styled'
import { useStateOptions } from './hooks/useStateOptions'
import { useProfiles } from './hooks/useProfiles'

interface Params {
  courseId: string
}

interface Props {
  onSubmit: (values: OnSubmitValues, onlySave: boolean) => void
  initialValues?: ICourseModel
  coursePermission?: ICoursePermissions
}

const CourseFormBase: React.FC<Props> = ({
  onSubmit,
  initialValues,
  coursePermission,
}) => {
  const { t } = useTranslation()
  const { courseId } = useParams<Params>()
  const history = useHistory()
  const { values, setValues, setFieldValue, errors, setErrors, valid } =
    useForm()
  const courseState = values.state?.value
  const isImparted = courseState === CourseStateType.Imparted
  const isPublished = courseState === CourseStateType.Published

  const { description } = values
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)

  const [isLinking, setIsLinking] = useState(false)
  const { integrationCoursera } = useIntegrations()

  const setFields = React.useCallback(
    (values: Partial<CreateNewCourseForm>) => {
      setValues((current) => ({ ...current, ...values }))
    },
    [setValues]
  )

  const isSynchronous = values.courseSynchronous
  const externalIntegrationField = useFieldExternalIntegration()
  const externalIntegration = externalIntegrationField.value
  const hasExternalIntegration = !!externalIntegration?.state
  const isEdit = !!initialValues?.id

  const hasSence = useHasSence()
  const hasLessons = !!initialValues?.lessons.length
  const {
    stateOptions,
    stateOptionsBase,
    publishedErrors,
    draftOption,
    importedOption,
  } = useStateOptions({
    untilDate: values.untilDate,
    showDates: values.showDates,
    hasLessons,
    isSynchronous,
    hasExternalIntegration,
  })

  useEffect(() => {
    if (publishedErrors.length > 0 && isPublished)
      setFields({ state: draftOption })
  }, [setFields, publishedErrors, isPublished, draftOption])

  const { profiles, hasProfilesLimited } = useProfiles({ coursePermission })

  useEffect(() => {
    if (initialValues) {
      const { startDate, untilDate, synchronousCourse } = initialValues
      const { maximumGrade, minimumGrade } = {
        ...synchronousCourse,
      }
      const { synchronousCourseSource } = { ...synchronousCourse }
      const courseType = initialValues.courseType || defaultValues.courseType

      const initial: CreateNewCourseForm = {
        ...initialValues,
        startDate: startDate ? moment(startDate).toDate() : undefined,
        untilDate: untilDate ? moment(untilDate).toDate() : undefined,
        state: getSelected(initialValues?.state || 0, stateOptionsBase, false),
        optionalProfiles:
          getSelected(initialValues.optionalProfiles, profiles, true) || [],
        requiredProfiles:
          getSelected(initialValues.requiredProfiles, profiles, true) || [],
        courseType,
        showDates:
          initialValues.startDate || initialValues.untilDate
            ? ShowDates.active
            : ShowDates.inactive,
        banner: initialValues.bannerData
          ? { name: initialValues.bannerData.metadata.filename }
          : undefined,
        thumbnail: initialValues.thumbnailData
          ? { name: initialValues.thumbnailData.metadata.filename }
          : undefined,
        canGenerateCertificate: initialValues.canGenerateCertificate
          ? initialValues.canGenerateCertificate
          : false,
        reminderNotification: initialValues.reminderNotification,
        reminderNoStartCourseNotification:
          initialValues.reminderNoStartCourseNotification,
        courseExternal: synchronousCourseSource?.value,
        courseTypeSynchronous: synchronousCourse?.synchronousCourseType,
        courseSynchronousProperties: {
          min: String(minimumGrade || ''),
          max: String(maximumGrade || ''),
        },
        courseSynchronous: !!synchronousCourse,
        hours: initialValues.hours ?? '',
        integrations: {
          sence: initialValues.senceIntegration,
          external: {
            id: initialValues['integrationCourseId'],
            link: initialValues['integrationCourseLink'],
            state: !!initialValues['integrationCourseLink'],
          },
        },
      }
      setValues({
        ...defaultValues,
        ...initial,
        description: initialValues.description || '',
      })
    }
  }, [initialValues, profiles, setValues, stateOptionsBase])

  // TODO: Encontrar una mejor forma de limpiar las variables de error
  useEffect(() => {
    if (values.name !== '') setErrors((base) => ({ ...base, ['name']: '' }))
  }, [setErrors, values.name])

  useEffect(() => {
    if (description !== '')
      setErrors((base) => ({ ...base, ['description']: '' }))
  }, [description, setErrors])

  useEffect(() => {
    dispatch({ type: fromProfilesActions.LMS_ALL_PROFILES_LIST })
  }, [dispatch])

  useEffect(() => {
    return () => {
      setLoading(false)
    }
  }, [])

  const isActiveCoursera = useMemo(() => {
    const config = integrationCoursera?.config
    return !!(config?.appKey && config?.appSecret && config?.orgId)
  }, [integrationCoursera])

  const requiredProfiles = values.requiredProfiles || []
  const optionalProfiles = values.optionalProfiles || []

  const totalProfiles = requiredProfiles.length + optionalProfiles.length

  const disabledSave =
    loading || (hasProfilesLimited && totalProfiles === 0) || !valid

  const onImgUpload = (file: any, origin: string) => {
    if (file) {
      imgFileValidate(file).then((response) => {
        response !== ImgStatusEnum.success
          ? ErrorToast(
              t(`administration.courses.new.form.validations.image.${response}`)
            )
          : setValues({ ...values, [origin]: file })
      })
    } else {
      setValues({ ...values, [origin]: '_destroy' })
    }
  }
  const hideDates = values.showDates === ShowDates.inactive

  const reminderNotification = React.useMemo(
    () => values.reminderNotification,
    [values.reminderNotification]
  )

  const handleSubmit = (onlySave = false) => {
    let newErrors: any = validate({ ...values, description })
    if (hasExternalIntegration && !externalIntegration?.link) {
      newErrors = {
        ...newErrors,
        integrationsExternalLink: t(
          'administration.courses.new.form.linkExternalCourse.errors.urlRequired'
        ),
      }
    }
    if (hasExternalIntegration && !externalIntegration.id) {
      newErrors = {
        ...newErrors,
        integrationsExternalLink: t(
          'administration.courses.new.form.linkExternalCourse.errors.linkUrlRequired'
        ),
      }
    }
    setErrors(newErrors)
    let canContinue = true

    for (const field in newErrors) {
      // @ts-ignore
      if (newErrors[field] !== '') {
        canContinue = false
        break
      }
    }

    if (canContinue) {
      setLoading(true)
      let dataToSend: CreateNewCourseForm = {
        ...values,
        description,
        reminderNotification,
      }
      dataToSend =
        values.courseType === CourseType.Open
          ? { ...dataToSend, directChiefApproval: false }
          : dataToSend
      dataToSend = hideDates
        ? { ...dataToSend, startDate: undefined, untilDate: undefined }
        : dataToSend
      onSubmit(dataToSend, onlySave)
      onlySave && SuccessToast(t('administration.toastMessages.editSuccess'))
    }
  }

  const joditConfig = {
    buttons: 'bold,italic,underline,strikethrough,|,image,link',
  }

  const handlePreview = () => {
    window.open(`/administration/courses/${courseId}/preview`)
  }

  const handleLinkExternalCourse = () => {
    const url = externalIntegration?.link

    if (!url) {
      setErrors({
        ...errors,
        integrationsExternalLink: t(
          'administration.courses.new.form.linkExternalCourse.errors.urlRequired'
        ),
      })
      return
    }

    const validUrl = /^(ftp|http|https):\/\/[^ "]+$/.test(url)
    if (!validUrl) {
      setErrors({
        ...errors,
        integrationsExternalLink: t(
          'administration.courses.new.form.linkExternalCourse.errors.invalidUrl'
        ),
      })
      return
    }
    setIsLinking(true)
    LinkExternalCourse(url)
      .then(({ data }) => {
        setValues({
          ...values,
          integrations: {
            ...values.integrations,
            external: {
              state: true,
              id: data.id,
              link: url,
            },
          },
          name: data.name,
          description: data.description,
          hours: data.hours,
        })
        SuccessToast(
          t('administration.courses.new.form.linkExternalCourse.success')
        )
      })
      .catch(({ response }) => {
        switch (response.data.error_message) {
          case 'linked to another course': {
            const translation = t(
              'administration.courses.new.form.linkExternalCourse.errors.linkedToAnotherCourseV3'
            )
            ErrorToast(
              translation.replace(
                '{courseName}',
                `"${response.data.course_name}"`
              )
            )
            break
          }
          default:
            ErrorToast(
              t(
                'administration.courses.new.form.linkExternalCourse.errors.default'
              )
            )
            break
        }
      })
      .finally(() => setIsLinking(false))
  }

  const changeCourseType = (value: boolean) => {
    externalIntegrationField.change({
      ...externalIntegration,
      state: value,
      link: value ? externalIntegration?.link : '',
    })
    if (!value) setErrors((base) => ({ ...base, integrationsExternalLink: '' }))
  }

  return (
    <Wrapper>
      <Row className={'d-flex justify-content-between'}>
        <Col
          xs={12}
          md={5}
          className={`p-0 ${styles.noMargin} ${styles.principalCol}`}
        >
          {integrationCoursera?.active && isActiveCoursera && (
            <>
              <Form.Group>
                <div
                  className={`d-flex justify-content-between ${
                    hasExternalIntegration && styles.linkExternalSwitch
                  }`}
                >
                  <InputLabel
                    label={
                      <>
                        {t(
                          'administration.courses.new.form.linkExternalCourse.label'
                        )}
                        <CommonToolTip
                          text={t(
                            'administration.courses.new.form.linkExternalCourse.tooltip'
                          )}
                          position="right"
                        >
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            className={`ml-2 ${styles.infoIcon}`}
                          />
                        </CommonToolTip>
                      </>
                    }
                  />
                  <Switch
                    className={styles.certificateSwitchBtn}
                    checked={hasExternalIntegration}
                    checkedChildren={t('administration.courses.new.form.yes')}
                    unCheckedChildren={t('administration.courses.new.form.no')}
                    onChange={(checked: boolean) => changeCourseType(checked)}
                    disabled={
                      initialValues?.state === CourseStateType.Published ||
                      isLinking ||
                      (initialValues?.lessons.length || 0) > 0
                    }
                  />
                </div>
              </Form.Group>
            </>
          )}
          {hasExternalIntegration &&
            integrationCoursera?.active &&
            isActiveCoursera && (
              <Fragment>
                <Form.Group>
                  <TextInput
                    name={'integrationsExternalLink'}
                    value={externalIntegration.link}
                    label={t(
                      'administration.courses.new.form.linkExternalCourse.urlLabel'
                    )}
                    onChange={(e) => {
                      externalIntegrationField.change({
                        ...externalIntegration,
                        link: e.target.value,
                      })
                    }}
                    error={errors.integrationsExternalLink}
                    className="mb-2"
                    disabled={isEdit}
                  />
                </Form.Group>
                <Form.Group>
                  <button
                    className={styles.linkExternalCourse}
                    disabled={
                      initialValues?.state === CourseStateType.Published ||
                      isLinking
                    }
                    onClick={handleLinkExternalCourse}
                  >
                    <FontAwesomeIcon
                      icon={isLinking ? faSpinnerThird : faRotate}
                      spin={isLinking}
                    />
                    {t(
                      'administration.courses.new.form.linkExternalCourse.buttonLabel'
                    )}
                  </button>
                </Form.Group>
              </Fragment>
            )}
          {integrationCoursera?.active && isActiveCoursera && (
            <Divider className={styles.linkExternalDivider}></Divider>
          )}
          <Form.Group>
            <TextInput
              name={'name'}
              value={values['name']}
              label={t('administration.courses.new.form.name')}
              onChange={(e: any) =>
                setValues({ ...values, [e.target.name]: e.target.value })
              }
              error={errors['name']}
              className="mb-2"
              disabled={hasExternalIntegration}
            />
          </Form.Group>
          <Form.Group>
            <RichTextInput
              name="description"
              value={description}
              label={
                <>
                  {t('administration.courses.new.form.description.label')}
                  <CommonToolTip
                    text={t(
                      'administration.courses.tooltips.courseDescription'
                    )}
                    position="right"
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className={`ml-2 ${styles.infoIcon}`}
                    />
                  </CommonToolTip>
                </>
              }
              onChange={(value: any) => {
                setFieldValue('description', value)
              }}
              config={joditConfig}
              error={errors['description']}
              disabled={hasExternalIntegration}
            />
          </Form.Group>
          <Form.Group className="mb-2 d-flex align-items-center">
            <InputLabel
              label={
                <>
                  {t('administration.courses.new.form.images.label')}
                  <CommonToolTip
                    text={t('administration.courses.tooltips.courseImage')}
                    position="right"
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className={`ml-2 ${styles.infoIcon}`}
                    />
                  </CommonToolTip>
                </>
              }
            />
          </Form.Group>
          <Form.Group as={Row} className="mb-3">
            <FileInput
              name={'banner'}
              value={values['banner']}
              label={t('administration.courses.new.form.images.banner')}
              onChange={(e: any) => onImgUpload(e, 'banner')}
              comment={t(
                'administration.courses.new.form.images.BannerSuggestedSize'
              )}
              error={errors['banner']}
              disabled={hasExternalIntegration}
            />
          </Form.Group>
          <Form.Group className="mb-4" as={Row}>
            <FileInput
              name={'thumbnail'}
              value={values['thumbnail']}
              label={t('administration.courses.new.form.images.thumbnail')}
              onChange={(e: any) => onImgUpload(e, 'thumbnail')}
              comment={t(
                'administration.courses.new.form.images.ThumbnailSuggestedSizeNew'
              )}
              error={errors['thumbnail']}
              disabled={hasExternalIntegration}
            />
          </Form.Group>
          <Form.Group>
            <InputLabel
              label={
                <Subtitle>
                  {t('administration.courses.new.form.courseType.label')}
                </Subtitle>
              }
            />
          </Form.Group>
          <CourseTypeInput
            value={values.courseType}
            onChange={(newValue) => {
              const newValues: Partial<CreateNewCourseForm> = {
                courseType: newValue,
              }

              setFields(newValues)
            }}
            error={errors.courseType}
            disabled={hasExternalIntegration}
          />
          {values['courseType'] === CourseType.Closed && (
            <Form.Group>
              <RequiresApproval>
                <span>
                  {t(
                    'administration.courses.new.form.directChiefApproval.labelV2'
                  )}
                </span>
                <Switch
                  checked={values['directChiefApproval']}
                  onChange={(checked) =>
                    setValues({ ...values, ['directChiefApproval']: checked })
                  }
                  checkedChildren={t(
                    'administration.courses.new.form.directChiefApproval.yesV2'
                  )}
                  unCheckedChildren={t(
                    'administration.courses.new.form.directChiefApproval.no'
                  )}
                />
              </RequiresApproval>
            </Form.Group>
          )}
          <SynchronousCheckBox
            courseId={courseId}
            disabled={hasExternalIntegration || courseId?.length > 0}
          />
          <Options disabled={hasExternalIntegration} />
          {hasSence && <Sence disabled={hasExternalIntegration} />}
        </Col>
        <Col xs={12} md={5} className={`px-0 ${styles.principalCol}`}>
          <Form.Group className="m-0">
            <InputLabel
              label={
                <>
                  {t('administration.courses.new.form.dates.courseDates')}
                  <CommonToolTip
                    text={t('administration.courses.tooltips.courseDates')}
                    position="right"
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className={`ml-2 ${styles.infoIcon}`}
                    />
                  </CommonToolTip>
                </>
              }
            />
          </Form.Group>
          <Form.Group className="m-0">
            <CheckBoxRadioButtonInput
              name={'showDates-0'}
              value={ShowDates.active}
              checked={
                hasExternalIntegration
                  ? false
                  : values['showDates'] === ShowDates.active
              }
              inline={true}
              type={'radio'}
              label={t('administration.courses.new.form.dates.withDates')}
              onChange={() =>
                setValues({ ...values, ['showDates']: ShowDates.active })
              }
              error={''}
              disabled={hasExternalIntegration}
            />
            <CheckBoxRadioButtonInput
              name={'showDates-1'}
              value={ShowDates.inactive}
              checked={
                hasExternalIntegration
                  ? false
                  : values['showDates'] === ShowDates.inactive
              }
              inline={true}
              type={'radio'}
              label={t('administration.courses.new.form.dates.withoutDates')}
              onChange={() => {
                if (isImparted) {
                  setValues({
                    ...values,
                    state: draftOption,
                    ['showDates']: ShowDates.inactive,
                    reminderNotification: undefined,
                  })
                  return
                }
                setValues({
                  ...values,
                  ['showDates']: ShowDates.inactive,
                  reminderNotification: undefined,
                })
              }}
              error={''}
              disabled={hasExternalIntegration}
            />
          </Form.Group>
          {!hideDates && (
            <Row className="m-0 mt-2" noGutters>
              <Form.Group as={Col} xs={6} className={'mb-0 pr-1'}>
                <DatePickerInput
                  name={'startDate'}
                  value={values['startDate']}
                  maxDate={values['untilDate']}
                  label={t('administration.courses.new.form.dates.inital')}
                  onChange={(date: any) => {
                    setValues({ ...values, ['startDate']: date })
                  }}
                  error={errors['startDate']}
                  popperClassName={styles.datePicker}
                  disabled={hasExternalIntegration}
                />
              </Form.Group>
              <Form.Group as={Col} xs={6} className={'mb-0 pl-1'}>
                <InputLabel
                  label={t('administration.courses.new.form.dates.hour')}
                />
                <TimePicker
                  format="HH:mm"
                  className={styles.timePicker}
                  onChange={(time) => {
                    setFieldValue('startDate', time?.toDate())
                  }}
                  value={
                    (values.startDate && moment(values.startDate)) || undefined
                  }
                  allowClear={false}
                  placeholder="HH:mm"
                  disabled={hasExternalIntegration}
                />
              </Form.Group>
              <Form.Group as={Col} xs={12} className={'mb-0 mt-2'}>
                <DatePickerInput
                  name={'untilDate'}
                  value={values['untilDate']}
                  label={t('administration.courses.new.form.dates.close')}
                  minDate={values['startDate']}
                  onChange={(date) => {
                    if (
                      isImparted &&
                      values.untilDate &&
                      date > values.untilDate &&
                      isImparted
                    ) {
                      setValues({
                        ...values,
                        state: draftOption,
                        untilDate: date,
                      })
                      return
                    }
                    if (isPublished && date < new Date()) {
                      setValues({
                        ...values,
                        state: importedOption,
                        untilDate: date,
                      })
                      return
                    }
                    setValues({ ...values, untilDate: date })
                  }}
                  error={errors['untilDate']}
                  popperClassName={styles.datePicker}
                  disabled={hasExternalIntegration}
                />
              </Form.Group>
            </Row>
          )}
          {isSynchronous && (
            <NotificationsInput
              config={reminderNotification?.config}
              active={!!reminderNotification?.active}
              onChange={(value) => {
                setFieldValue('reminderNotification', {
                  active: !!value,
                  emailBody: reminderNotification?.emailBody || '',
                  config: value ? { delayTime: value.reminder } : undefined,
                })
              }}
              error={errors.courseType}
              className="mt-2"
              disabled={hideDates || hasExternalIntegration}
            />
          )}
          <Form.Group className={'mb-0 mt-1'}>
            <SelectInput
              name="optionalProfiles"
              label={
                <>
                  {t('administration.courses.new.form.lmsOptionalProfiles')}
                  <CommonToolTip
                    text={t(
                      'administration.courses.tooltips.courseLMSProfiles'
                    )}
                    position="right"
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className={`ml-2 ${styles.infoIcon}`}
                    />
                  </CommonToolTip>
                </>
              }
              options={profiles}
              closeMenuOnSelect={false}
              value={values['optionalProfiles']}
              isMulti={true}
              withColors={true}
              onChange={(selected: any) =>
                setValues({ ...values, ['optionalProfiles']: selected })
              }
              error={errors['optionalProfiles']}
            />
          </Form.Group>
          <Form.Group className={'mb-0 mt-1'}>
            <SelectInput
              name="requiredProfiles"
              label={
                <>
                  {t('administration.courses.new.form.lmsRequiredProfiles')}
                  <CommonToolTip
                    text={t(
                      'administration.courses.tooltips.courseMandatoriesLMSProfiles'
                    )}
                    position="right"
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className={`ml-2 ${styles.infoIcon}`}
                    />
                  </CommonToolTip>
                </>
              }
              options={profiles}
              closeMenuOnSelect={false}
              value={values['requiredProfiles']}
              isMulti={true}
              withColors={true}
              onChange={(selected: any) =>
                setValues({ ...values, ['requiredProfiles']: selected })
              }
              error={errors['requiredProfiles']}
            />
          </Form.Group>
          <Col xs={12} md={5} className={`px-0 my-1 ${styles.principalCol}`}>
            <Form.Group className={`${styles.certificateFormGroup}`}>
              <div className="d-flex align-items-center mb-1">
                <InputLabel
                  className="mb-0"
                  label={
                    <>
                      {t('administration.courses.new.form.certificates.title')}
                      <CommonToolTip
                        text={t(
                          'administration.courses.tooltips.courseCertificated'
                        )}
                        position="right"
                      >
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className={`ml-2 ${styles.infoIcon}`}
                        />
                      </CommonToolTip>
                    </>
                  }
                />
                <Switch
                  className={styles.certificateSwitchBtn}
                  checked={values['canGenerateCertificate']}
                  checkedChildren={t('administration.courses.new.form.yes')}
                  unCheckedChildren={t('administration.courses.new.form.no')}
                  onChange={(checked: boolean) =>
                    setValues({
                      ...values,
                      ['canGenerateCertificate']: checked,
                    })
                  }
                />
              </div>
              <span className={styles.certificateMsg}>
                {values['canGenerateCertificate']
                  ? initialValues?.individualCertificatesName
                    ? t(
                        'administration.courses.new.form.certificates.individual',
                        {
                          name: initialValues?.individualCertificatesName,
                          count: initialValues?.grupalCertificatesCount,
                        }
                      )
                    : t('administration.courses.new.form.certificates.create')
                  : t('administration.courses.new.form.certificates.grupal', {
                      count: initialValues?.grupalCertificatesCount,
                    })}
              </span>
            </Form.Group>
          </Col>
          <Form.Group className={'mb-0'}>
            <div className="row no-gutters">
              <div className="col">
                <SelectInput
                  name="state"
                  label={t('administration.courses.new.form.state')}
                  placeHolder={t(
                    'administration.courses.new.form.placeholders.state'
                  )}
                  options={stateOptions}
                  closeMenuOnSelect={true}
                  value={values.state}
                  isMulti={false}
                  withColors={false}
                  onChange={(selected) =>
                    setValues({ ...values, ['state']: selected })
                  }
                  error={errors['state']}
                  disabledReason={(option) => {
                    if (!option.disabled) return null

                    if (option.value === CourseStateType.Imparted) return null

                    if (option.value === CourseStateType.Published) {
                      const [publishedError] = publishedErrors
                      if (publishedError) return `(${publishedError})`
                    }
                  }}
                />
              </div>
              {courseState === CourseStateType.Soon &&
                (!values.startDate || hideDates) && (
                  <div className="col-auto d-flex align-items-center">
                    <div className="mt-4">
                      <CommonToolTip
                        text={t(
                          'administration.courses.tooltips.soonWithOutDate'
                        )}
                        position="left"
                        className={styles.soonWithOutDateTooltip}
                      >
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className={`ml-2 ${styles.infoIcon} color-warning`}
                        />
                      </CommonToolTip>
                    </div>
                  </div>
                )}
            </div>
          </Form.Group>
          <EmailNotifications
            isNewCourse={!initialValues}
            disableReminder={false}
          />
        </Col>
      </Row>
      <Row className={`pt-4 ${styles.actionButtons}`}>
        <Col className="p-0">
          <ButtonToolbar className="float-left ">
            <Button
              variant={ButtonVariant.Secondary}
              size={ButtonSizes.Small}
              className="ml-0"
              onClick={() => history.push(`/administration/courses/`)}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              {t('administration.lessons.buttons.back')}
            </Button>
          </ButtonToolbar>
          <ButtonToolbar className="float-right ">
            {courseId && (
              <Button
                disabled={loading}
                variant={ButtonVariant.Secondary}
                size={ButtonSizes.Small}
                onClick={handlePreview}
              >
                <FontAwesomeIcon icon={faEye} className="mr-2" />
                {t('administration.lessons.buttons.preview')}
              </Button>
            )}
            <Button
              disabled={disabledSave}
              variant={ButtonVariant.Success}
              size={ButtonSizes.Small}
              onClick={() => handleSubmit(true)}
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
              ) : (
                <FontAwesomeIcon icon={faSave} className="mr-2" />
              )}
              {t('administration.courses.new.form.buttons.saveAndExit')}
            </Button>
            {!isSynchronous && (
              <Button
                disabled={disabledSave || hasExternalIntegration}
                size={ButtonSizes.Small}
                className="mr-0 "
                onClick={() => handleSubmit()}
              >
                {loading ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                ) : (
                  <FontAwesomeIcon icon={faArrowRight} className="mr-2" />
                )}
                {t('administration.courses.new.form.buttons.goTolessons')}
              </Button>
            )}
          </ButtonToolbar>
        </Col>
      </Row>
    </Wrapper>
  )
}

const CourseForm: React.FC<Props> = (props) => {
  return (
    <FormProvider>
      <CourseFormBase {...props} />
    </FormProvider>
  )
}

export default CourseForm
